import { createSlice } from '@reduxjs/toolkit';
import {
    LoadMarketplacePricing,
    LoadMarketplaceTemplatePricing,
} from './load-marketplace-pricing.thunk';
import { MarketplacePricingState } from './marketplace-pricing.state';
import { FeeRefundStatus } from './models/fee-refund-status.enum';

const initialState: MarketplacePricingState = {
    pricing: {
        requestPreorderAgreementButtonText: '',
        engagementId: 0,
        isTemplate: false,
        isWalletCreditsForMonthlyFeesAllowed: true,
        considerationSettings: [],
        discountBrokeragePricing: {
            feeCalculatorSettings: {
                innerRevenueMultiplePercent: 0,
                innerValuePerDvmPercent: 0,
                outerRevenueMultiplePercent: 0,
                outerValuePerDvmPercent: 0,
                middleOfRangeRevenueMultiple: 0,
                middleOfRangeValuePerDvm: 0,
            },
            transactionFeeSchedule: [],
            transactionFees: {
                idNdaFirstXCount: 0,
                idNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                idNdaFeeCreditable: 0,
                dataNdaFirstXCount: 0,
                dataNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                dataNdaFeeCreditable: 0,
                financialModelFirstXCount: 0,
                financialModelFeeRefundable: FeeRefundStatus.NonRefundable,
                financialModelFeeCreditable: 0,
                buyerMeetingVirtualCredit: 0,
                buyerMeetingInPersonCredit: 0,
                buyerMeetingRefundable: FeeRefundStatus.NonRefundable,
                buyerMeetingCreditable: 0,
                applicationFee: 0,
                applicationFeePerDvm: 0,
                applicationFeeRefundable: FeeRefundStatus.NonRefundable,
                applicationFeeCreditable: 0,
                lateFeeRefundable: FeeRefundStatus.NonRefundable,
                lateFeeCreditable: 0,
                tailPeriodMonths: 0,
            },
            buyersFees: {
                sellerIdFeePerDvm: 0,
                sellerIdFeeRefundable: FeeRefundStatus.NonRefundable,
                pipAccessFeePerDvm: 0,
                pipAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                dfmAccessFeePerDvm: 0,
                dfmAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                sellerMeetingFeeVirtual: 0,
                sellerMeetingFeeInPerson: 0,
                sellerMeetingFeeRefundable: FeeRefundStatus.NonRefundable,
                dateModified: new Date(),
            },
        },
        fullServiceBrokeragePricing: {
            feeCalculatorSettings: {
                innerRevenueMultiplePercent: 0,
                innerValuePerDvmPercent: 0,
                outerRevenueMultiplePercent: 0,
                outerValuePerDvmPercent: 0,
                middleOfRangeRevenueMultiple: 0,
                middleOfRangeValuePerDvm: 0,
            },
            transactionFeeSchedule: [],
            transactionFees: {
                idNdaFirstXCount: 0,
                idNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                idNdaFeeCreditable: 0,
                dataNdaFirstXCount: 0,
                dataNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                dataNdaFeeCreditable: 0,
                financialModelFirstXCount: 0,
                financialModelFeeRefundable: FeeRefundStatus.NonRefundable,
                financialModelFeeCreditable: 0,
                buyerMeetingVirtualCredit: 0,
                buyerMeetingInPersonCredit: 0,
                buyerMeetingRefundable: FeeRefundStatus.NonRefundable,
                buyerMeetingCreditable: 0,
                applicationFee: 0,
                applicationFeePerDvm: 0,
                applicationFeeRefundable: FeeRefundStatus.NonRefundable,
                applicationFeeCreditable: 0,
                lateFeeRefundable: FeeRefundStatus.NonRefundable,
                lateFeeCreditable: 0,
                tailPeriodMonths: 0,
            },
            buyersFees: {
                sellerIdFeePerDvm: 0,
                sellerIdFeeRefundable: FeeRefundStatus.NonRefundable,
                pipAccessFeePerDvm: 0,
                pipAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                dfmAccessFeePerDvm: 0,
                dfmAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                sellerMeetingFeeVirtual: 0,
                sellerMeetingFeeInPerson: 0,
                sellerMeetingFeeRefundable: FeeRefundStatus.NonRefundable,
                dateModified: new Date(),
            },
        },
        sfasPricing: {
            feeCalculatorSettings: {
                innerRevenueMultiplePercent: 0,
                innerValuePerDvmPercent: 0,
                outerRevenueMultiplePercent: 0,
                outerValuePerDvmPercent: 0,
                middleOfRangeRevenueMultiple: 0,
                middleOfRangeValuePerDvm: 0,
            },
            transactionFeeSchedule: [],
            transactionFees: {
                idNdaFirstXCount: 0,
                idNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                idNdaFeeCreditable: 0,
                dataNdaFirstXCount: 0,
                dataNdaFeeRefundable: FeeRefundStatus.NonRefundable,
                dataNdaFeeCreditable: 0,
                financialModelFirstXCount: 0,
                financialModelFeeRefundable: FeeRefundStatus.NonRefundable,
                financialModelFeeCreditable: 0,
                buyerMeetingVirtualCredit: 0,
                buyerMeetingInPersonCredit: 0,
                buyerMeetingRefundable: FeeRefundStatus.NonRefundable,
                buyerMeetingCreditable: 0,
                applicationFee: 0,
                applicationFeePerDvm: 0,
                applicationFeeRefundable: FeeRefundStatus.NonRefundable,
                applicationFeeCreditable: 0,
                lateFeeRefundable: FeeRefundStatus.NonRefundable,
                lateFeeCreditable: 0,
                tailPeriodMonths: 0,
            },
            buyersFees: {
                sellerIdFeePerDvm: 0,
                sellerIdFeeRefundable: FeeRefundStatus.NonRefundable,
                pipAccessFeePerDvm: 0,
                pipAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                dfmAccessFeePerDvm: 0,
                dfmAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                sellerMeetingFeeVirtual: 0,
                sellerMeetingFeeInPerson: 0,
                sellerMeetingFeeRefundable: FeeRefundStatus.NonRefundable,
                dateModified: new Date(),
            },
        },
    },
    isLoading: false,
    status: 'loading',
    error: '',
};

const marketplacePricingSlice = createSlice({
    name: 'marketplacePricing',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(LoadMarketplaceTemplatePricing.pending, (state) =>
                setPendingState(state)
            )
            .addCase(
                LoadMarketplaceTemplatePricing.fulfilled,
                (state, action) => {
                    if (!action.payload?.data) {
                        setRejectedState(state);
                        return;
                    }
                    const pricing = action.payload.data;
                    if (pricing.sfasPricing && !pricing.sfasPricing.buyersFees) {
                        pricing.sfasPricing.buyersFees = {
                            sellerIdFeePerDvm: 0,
                            sellerIdFeeRefundable: FeeRefundStatus.NonRefundable,
                            pipAccessFeePerDvm: 0,
                            pipAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                            dfmAccessFeePerDvm: 0,
                            dfmAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                            sellerMeetingFeeVirtual: 0,
                            sellerMeetingFeeInPerson: 0,
                            sellerMeetingFeeRefundable: FeeRefundStatus.NonRefundable,
                            dateModified: new Date(),
                        };
                    }
                    state.pricing = pricing;
                    setSucceededState(state);
                }
            )
            .addCase(LoadMarketplaceTemplatePricing.rejected, (state) =>
                setRejectedState(state)
            )
            .addCase(LoadMarketplacePricing.pending, (state) =>
                setPendingState(state)
            )
            .addCase(LoadMarketplacePricing.fulfilled, (state, action) => {
                if (!action.payload?.data) {
                    setRejectedState(state);
                    return;
                }
                const pricing = action.payload.data;
                if (pricing.sfasPricing && !pricing.sfasPricing.buyersFees) {
                    pricing.sfasPricing.buyersFees = {
                        sellerIdFeePerDvm: 0,
                        sellerIdFeeRefundable: FeeRefundStatus.NonRefundable,
                        pipAccessFeePerDvm: 0,
                        pipAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                        dfmAccessFeePerDvm: 0,
                        dfmAccessFeeRefundable: FeeRefundStatus.NonRefundable,
                        sellerMeetingFeeVirtual: 0,
                        sellerMeetingFeeInPerson: 0,
                        sellerMeetingFeeRefundable: FeeRefundStatus.NonRefundable,
                        dateModified: new Date(),
                    };
                }
                state.pricing = pricing;
                setSucceededState(state);
            })
            .addCase(LoadMarketplacePricing.rejected, (state) =>
                setRejectedState(state)
            );
    },
});

const setRejectedState = (state: any) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = state;
};

const setPendingState = (state: any) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: any) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};
export const {} = marketplacePricingSlice.actions;
export default marketplacePricingSlice.reducer;
